/**
 * 호스트(카드)별 사이트 프로필.
 *
 * 여러 서브도메인이 같은 앱·같은 dist 를 쓰지만 지점(branch) 설정에 따라 브랜드·매장 데이터가 다르다.
 * 검색엔진이 "같은 사이트 N벌"로 보지 않도록 카드에 따라 달라지는 페이지(홈·브랜드 목록·브랜드 상세·매장 검색·쇼핑)의
 * 껍데기(헤더 H1, 상단 안내 문장, title/description)를 카드명으로 치환하고 canonical 은 자기 호스트로 둔다.
 * 카드와 무관하게 내용이 같은 페이지(약관·개인정보·FAQ·공지·문의·소개)는 파트너 호스트에서 noindex + canonical→bridgem 으로 보낸다.
 *
 * 문구는 운영에서 자유롭게 고친다. kbprm·narasarang 은 카드사 심의 문구를 intro·subject·payWith·mechanism·closing 조각으로 그대로 두고,
 * 다른 호스트와 같은 구성(SiteIntro·BrandSeoSummary·JSON-LD)에 그 조각을 끼워 넣는다 (2026-10-26. 심의 문구 블록은 모달 안이라 크롤러 스냅샷에 들어가지 않았음).
 */
export const MAIN_ORIGIN = "https://bridgem.hivitamin.co.kr";

/**
 * 카드에 따라 데이터가 달라지는 경로 접두사. 이 밖의 경로는 모든 호스트에서 내용이 같다.
 * 예외: /HFO_HI_002(청구할인 혜택 정리)는 프로필 benefitGuide 가 참인 호스트에서만 보여 주고 자기 호스트로 색인한다 (페이지가 origin 을 직접 넘긴다).
 * benefitGuide 는 진입 링크(메인 플로팅·푸터·FAQ) 노출 조건이기도 하다. 플래그가 없는 호스트는 페이지가 브랜드 목록으로 돌려보낸다.
 * - bridgem: 끔 (2026-11-04). 전체·업종별 브랜드 수가 경쟁사에 서비스 규모를 한눈에 주는 자료가 되고, 홈·FAQ 와 내용이 겹친다.
 * - kbprm·narasarang: 끔. 심의 문구 호스트.
 */
export const HOST_SPECIFIC_PREFIXES = ["/HFO_BI_001", "/HFO_BI_002", "/HFO_MS_001", "/HFO_SP_001"];

const PROFILES = {
	"bridgem.hivitamin.co.kr": {
		key: "bridgem",
		isMain: true,
		cardName: "복지카드",
		shortName: "복지카드",
		audience: "복지카드 회원",
		heading: "하이비타민 복지카드 제휴 가맹점 청구할인",
		intro: "복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 20% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.",
	},
	// KB 일반 복지카드 진입점. VIP(프라임+)는 kbprm 호스트. 문구는 우리카드와 같은 형식.
	"kb.hivitamin.co.kr": {
		key: "kb",
		cardName: "KB국민카드",
		shortName: "KB국민카드",
		audience: "KB국민카드 회원",
		heading: "KB국민카드 청구할인 제휴 브랜드",
		intro: "KB국민카드 복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 20% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.",
		payWith: "KB국민카드 복지카드",
		benefitGuide: true,
	},
	"kbprm.hivitamin.co.kr": {
		key: "kbprm",
		cardName: "KB국민카드 프라임+",
		shortName: "KB국민카드",
		audience: "KB국민카드 프라임+ 고객",
		heading: "KB국민카드 프라임+ 고객 전용 제휴가맹점 청구할인",
		// 아래 문구는 KB국민카드 준법감시 심의 문구 그대로. 수정 시 심의 범위 확인.
		intro: "본 서비스는 KB국민카드와 제휴된 (주)브릿지엠이 운영하는 Hi-비타민 서비스로, 프라임+ 고객은 별도 응모 없이 제휴가맹점에서 KB국민카드로 결제 시 2~10% 청구할인을 받을 수 있습니다.",
		// 브랜드 상세 요약 문단용: "{브랜드}에서 {subject} {payWith}로 결제하면 … {closing}"
		subject: "프라임+ 고객이",
		payWith: "KB국민카드",
		closing: "별도 응모 없이 적용되며, 카드 자체 할인서비스·이벤트 등 다른 청구할인 서비스와 중복 적용 가능합니다. 삼성페이, 페이코 등 간편결제는 할인이 적용되지 않을 수 있습니다.",
	},
	"nhbank.hivitamin.co.kr": {
		key: "nhbank",
		cardName: "NH농협카드",
		shortName: "농협카드",
		audience: "NH농협카드 회원",
		heading: "NH농협카드 청구할인 제휴 브랜드",
		intro: "NH농협카드 복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 20% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.",
		payWith: "NH농협카드 복지카드",
		benefitGuide: true,
	},
	"geps.hivitamin.co.kr": {
		key: "geps",
		cardName: "공무원연금공단 복지카드",
		shortName: "공무원연금공단",
		audience: "공무원연금공단(GEPS) 복지카드 회원",
		heading: "공무원연금공단 복지카드 청구할인 제휴 브랜드",
		intro: "공무원연금공단 복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 20% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.",
		payWith: "공무원연금공단 복지카드",
		benefitGuide: true,
	},
	"narasarang.hivitamin.co.kr": {
		key: "narasarang",
		cardName: "하나 나라사랑카드(체크)",
		shortName: "나라사랑카드",
		audience: "하나 나라사랑카드(체크) 손님",
		heading: "하나 나라사랑카드(체크) 손님 대상 청구할인 제휴 브랜드",
		// 아래 문구는 하나카드 준법심의(A-26-0080, 2026.01.13~2026.12.31) 문구 기준. 수정 시 심의 범위 확인.
		intro: "하나 나라사랑카드(체크) 손님 대상 혜택입니다. 군 급여 이체 조건 및 전월 실적 조건이 필요하지 않으며, 제휴사 상황에 따라 혜택이 변동될 수 있으니 이벤트 페이지에서 혜택을 확인하시기 바랍니다.",
		// 브랜드 상세 요약 문단용. 나라사랑카드 청구할인은 익월 차감이 아니라 캐시백(2영업일 뒤 결제계좌 입금)이다.
		payWith: "하나 나라사랑카드(체크)",
		mechanism: "청구할인 혜택이 캐시백으로 적용되어 2영업일 뒤 결제계좌로 입금됩니다.",
		closing: "군 급여 이체 조건 및 전월 실적 조건이 필요하지 않습니다. 간편결제수단 이용 시 청구할인 혜택을 받으실 수 없으며, 하나머니 결제 시 하나 나라사랑카드(체크)로 결제한 부분만 할인이 적용됩니다.",
		metaClosing: "군 급여 이체·전월 실적 조건 없음, 캐시백 적용, 가까운 매장 위치 안내.",
	},
	// 삼성카드 진입점. 문구는 우리카드와 같은 형식(할인율 상한만 지점 데이터 기준 7%).
	"samsung.hivitamin.co.kr": {
		key: "samsung",
		cardName: "삼성카드",
		shortName: "삼성카드",
		audience: "삼성카드 회원",
		heading: "삼성카드 청구할인 제휴 브랜드",
		intro: "삼성카드 복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 7% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.",
		payWith: "삼성카드 복지카드",
		benefitGuide: true,
	},
	"woori.hivitamin.co.kr": {
		key: "woori",
		cardName: "우리카드",
		shortName: "우리카드",
		audience: "우리카드 회원",
		heading: "우리카드 청구할인 제휴 브랜드",
		intro: "우리카드 복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 20% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.",
		payWith: "우리카드 복지카드",
		benefitGuide: true,
	},
};

const MAIN_PROFILE = PROFILES["bridgem.hivitamin.co.kr"];

/**
 * 프로필이 없는 운영 호스트(nhprm 등 별도 진입점이 아닌 서브도메인). 문구는 메인과 같게 보여 주되
 * 모든 페이지를 noindex + canonical→bridgem 으로 보내 bridgem 과 중복 색인되지 않게 한다.
 */
const UNLISTED_PROFILE = { ...MAIN_PROFILE, key: "unlisted", isMain: false, noindexAll: true };

/** 호스트명으로 프로필을 찾는다. bridgem·로컬은 메인, 등록된 파트너는 각자, 그 밖의 호스트는 색인 제외 프로필. */
export function profileForHost(hostname) {
	const host = String(hostname || "").toLowerCase();
	if (PROFILES[host]) return PROFILES[host];
	if (!host || host === "localhost" || host === "127.0.0.1" || host.startsWith("localhost:")) return MAIN_PROFILE;
	return UNLISTED_PROFILE;
}

export function isHostSpecificPath(path) {
	const normalized = (path || "/").replace(/\/+$/, "") || "/";
	if (normalized === "/") return true;
	return HOST_SPECIFIC_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(prefix + "/") || normalized.startsWith(prefix + "_"));
}
